/**
 * Pure stock allocation computation - sorts stock rows, computes FEFO
 * allocation suggestions, derives stock status, and enriches allocations
 * with batch display metadata.
 *
 * Accepts stock rows with ISO string dates (matching the Stock entity type).
 * Frontend-only; no database dependency.
 */

import type { BatchAllocationItem } from '@snootsvet/shared'
import { formatQuantity, pluralizeUnit, roundQuantity } from '@snootsvet/shared'

/**
 * Status for a stock allocation request, computed by `computeStockAllocation`.
 *
 * - `ok`: valid stock fully covers the request, FEFO suggestions provided
 * - `no-stock`: no batches exist at all for this product/clinic
 * - `insufficient-stock`: valid stock exists but not enough for the quantity
 * - `no-valid-stock`: batches exist but all are expired or depleted
 * - `mixed-unbatched`: unbatched stock exists but batched stock alone can't
 *   cover the request - user must allocate manually
 */
export type StockAllocationStatus =
  | 'ok'
  | 'no-stock'
  | 'insufficient-stock'
  | 'no-valid-stock'
  | 'mixed-unbatched'

/**
 * Display status for an individual stock row, computed by `computeStockAllocation`.
 *
 * - `valid`: qty > 0, not expired
 * - `expired`: qty > 0, expired
 * - `depleted`: qty <= 0, not expired
 * - `expired_depleted`: qty <= 0 and expired
 * - `unbatched_mixed`: unbatched row on a product with batched history
 */
export type StockRowStatus =
  | 'valid'
  | 'expired'
  | 'depleted'
  | 'expired_depleted'
  | 'unbatched_mixed'

type StockRow = {
  batchNumber: string | null
  quantity: number
  expiryDate: string | null
  createdAt: string
  updatedAt: string
}

/** A stock row enriched with a derived display status for the allocation UI. */
export type StockAllocationBatch = {
  batchNumber: string | null
  quantity: number
  expiryDate: string | null
  createdAt: string
  updatedAt: string
  status: StockRowStatus
}

/** A batch allocation enriched with status and expiry for display in the allocation editor. */
export interface EnrichedAllocationItem extends BatchAllocationItem {
  batchStatus?: StockRowStatus
  expiryDate?: string | null
}

/**
 * Assigns a sort tier to a stock row for the available batches list.
 *
 *   1. Valid stock (qty > 0, not expired) - best option, FEFO within tier
 *   2. Expired with stock (qty > 0, expired) - real stock, user's call
 *   3. Depleted, not expired (qty <= 0, not expired) - batch exists but empty
 *   4. Expired and depleted - worst option, shown for history
 */
function computeStockSortTier(
  row: { quantity: number; expiryDate: Date | null },
  now: Date
): number {
  const hasStock = row.quantity > 0
  const isExpired = row.expiryDate !== null && row.expiryDate <= now
  if (hasStock && !isExpired) return 1
  if (hasStock && isExpired) return 2
  if (!hasStock && !isExpired) return 3
  return 4
}

/** Default expiry warning window for `isExpiringSoon`. */
export const EXPIRY_WARNING_DAYS = 7

/**
 * Whether any stock row has a non-null batch number. Treat this as the
 * canonical "is this product (or this clinic's slice of it) usually
 * batch-tracked" primitive - shared by allocation flows and inventory
 * management UI.
 */
export function hasBatchedStock(rows: { batchNumber: string | null }[]): boolean {
  return rows.some((row) => row.batchNumber !== null)
}

/**
 * Whether any stock row has a non-null expiry date. Mirror of
 * `hasBatchedStock` - the canonical "is this product usually recorded with an
 * expiry date" primitive, derived from historic stock rather than a product
 * flag.
 */
export function productHasExpiryDate(rows: { expiryDate: string | null }[]): boolean {
  return rows.some((row) => row.expiryDate !== null)
}

/**
 * Breaks a raw subunit quantity into a human-readable pack + loose summary
 * (e.g. 215 tablets in boxes of 100 → "2 Boxes + 15 Tablets"). Returns null
 * when the breakdown would add nothing beyond the raw quantity - single-unit
 * products, or quantities that don't reach one whole pack.
 */
export function formatPackBreakdown(
  quantity: number,
  product: { packSize: number; packUnit: string; unit: string }
): string | null {
  if (product.packSize <= 1 || quantity <= 0) return null
  const packs = Math.floor(quantity / product.packSize)
  if (packs === 0) return null
  const loose = quantity - packs * product.packSize
  const parts = [`${packs} ${pluralizeUnit(product.packUnit, packs)}`]
  if (loose > 0) parts.push(`${formatQuantity(loose)} ${pluralizeUnit(product.unit, loose)}`)
  return parts.join(' + ')
}

/** Coerces a stock row's expiry value (string | Date | null) to Date | null. */
function toExpiryDate(expiryDate: Date | string | null): Date | null {
  if (expiryDate === null) return null
  return expiryDate instanceof Date ? expiryDate : new Date(expiryDate)
}

/**
 * Whether `expiryDate` falls within `daysAhead` days from `now` (but is not
 * already expired). Used as an orthogonal indicator on top of the row status -
 * a row can be `'valid'` and "expiring soon" at the same time.
 */
export function isExpiringSoon(
  expiryDate: Date | string | null,
  daysAhead: number = EXPIRY_WARNING_DAYS,
  now: Date = new Date()
): boolean {
  const expiry = toExpiryDate(expiryDate)
  if (expiry === null) return false
  const nowMs = now.getTime()
  const expiryMs = expiry.getTime()
  return expiryMs > nowMs && expiryMs <= nowMs + daysAhead * 24 * 60 * 60 * 1000
}

/**
 * Derives the display status for a single stock row. Pure - takes either a
 * Date or ISO string for expiry so it can be called from contexts that
 * haven't pre-parsed the row.
 *
 * `opts.hasBatchedStock` enables the `'unbatched_mixed'` classification
 * (relevant in allocation/dispense contexts where the unbatched row should
 * be skipped from FEFO suggestions); leave it false in display contexts
 * that surface unbatched rows separately.
 */
export function getStockRowStatus(
  row: { batchNumber: string | null; quantity: number; expiryDate: Date | string | null },
  opts: { hasBatchedStock?: boolean; now?: Date } = {}
): StockRowStatus {
  const { hasBatchedStock = false, now = new Date() } = opts
  if (row.batchNumber === null && hasBatchedStock) return 'unbatched_mixed'
  const expiry = toExpiryDate(row.expiryDate)
  const hasStock = row.quantity > 0
  const isExpired = expiry !== null && expiry <= now
  if (!hasStock && isExpired) return 'expired_depleted'
  if (!hasStock) return 'depleted'
  if (isExpired) return 'expired'
  return 'valid'
}

/** Maps a stock row status to a Tailwind text colour class. */
export function stockRowTextClass(status: StockRowStatus): string {
  switch (status) {
    case 'valid':
      return 'text-success-accent'
    case 'depleted':
    case 'unbatched_mixed':
      return 'text-warning-accent'
    case 'expired':
    case 'expired_depleted':
      return 'text-destructive-accent'
  }
}

/**
 * Sorts stock rows, computes a FEFO (first-expired, first-out) allocation
 * suggestion, derives a stock status, and enriches allocations with batch
 * display metadata.
 *
 * Returns:
 * - `allocations` - suggested batch assignments enriched with display metadata
 * - `availableBatches` - all stock rows sorted by tier then FEFO, with derived status
 * - `stockStatus` - one of:
 *   - `ok` - valid stock fully covers the request; allocations are populated
 *   - `insufficient-stock` - valid stock exists but not enough
 *   - `no-stock` - no stock rows exist at all for this product/clinic
 *   - `no-valid-stock` - stock rows exist but all are expired or depleted
 *   - `mixed-unbatched` - unbatched stock exists alongside batched history,
 *     but batched stock alone can't cover the request
 *
 * FEFO suggestions only draw from valid stock (qty > 0, not expired).
 * Unbatched rows are skipped when batched stock exists historically.
 *
 * @param rows - Stock rows for a single product/clinic combination.
 * @param quantity - Requested quantity to allocate.
 * @param now - Current time (defaults to new Date(); injectable for tests).
 */
export function computeStockAllocation(
  rows: StockRow[],
  quantity: number,
  now: Date = new Date()
): {
  allocations: EnrichedAllocationItem[]
  availableBatches: StockAllocationBatch[]
  stockStatus: StockAllocationStatus
} {
  // Round each stock level onto the 2dp grid up front. `stock.quantity` is a
  // float8 mutated in place by every intake/allocation/reversal, so live rows
  // drift (1.77 stored as 1.7699999999999996). Normalising here — rather than
  // at each point of use — keeps the suggested allocations, the tier
  // classification and the batch quantities we hand back mutually consistent.
  const parsed = rows.map((row) => ({
    ...row,
    quantity: roundQuantity(row.quantity),
    expiryDate: row.expiryDate ? new Date(row.expiryDate) : null,
    createdAt: new Date(row.createdAt),
    updatedAt: new Date(row.updatedAt),
  }))

  const sorted = [...parsed].sort((a, b) => {
    const tierDiff = computeStockSortTier(a, now) - computeStockSortTier(b, now)
    if (tierDiff !== 0) return tierDiff
    if (a.expiryDate === null && b.expiryDate === null) return 0
    if (a.expiryDate === null) return 1
    if (b.expiryDate === null) return -1
    return a.expiryDate.getTime() - b.expiryDate.getTime()
  })

  const validRows = sorted.filter((row) => computeStockSortTier(row, now) === 1)
  const hasValidStock = validRows.length > 0
  const productIsBatched = hasBatchedStock(sorted)
  const hasUnbatchedStock = sorted.some((row) => row.batchNumber === null)

  const rawAllocations: BatchAllocationItem[] = []
  let remaining = quantity

  for (const row of validRows) {
    if (remaining <= 0) break
    if (row.batchNumber === null && productIsBatched) continue
    const take = Math.min(row.quantity, remaining)
    // Defensive: rows are grid-aligned by now, so this only bites if a caller
    // passes an off-grid `quantity`. A zero allocation fails `.positive()`.
    if (take <= 0) continue
    rawAllocations.push({ batchNumber: row.batchNumber, quantity: take })
    // Round the running remainder — subtracting grid values still drifts
    // (2.64 - 1.77 = 0.8700000000000001), and it compounds across batches.
    remaining = roundQuantity(remaining - take)
  }

  const isMixedShortfall = remaining > 0 && productIsBatched && hasUnbatchedStock

  // Don't return partial suggestions — if we can't fully allocate,
  // the user must build the entire allocation manually.
  if (remaining > 0) rawAllocations.length = 0

  let stockStatus: StockAllocationStatus
  if (sorted.length === 0) {
    stockStatus = 'no-stock'
  } else if (!hasValidStock) {
    stockStatus = 'no-valid-stock'
  } else if (isMixedShortfall) {
    stockStatus = 'mixed-unbatched'
  } else if (remaining > 0) {
    stockStatus = 'insufficient-stock'
  } else {
    stockStatus = 'ok'
  }

  const availableBatches: StockAllocationBatch[] = sorted.map((row) => ({
    batchNumber: row.batchNumber,
    quantity: row.quantity,
    expiryDate: row.expiryDate?.toISOString() ?? null,
    createdAt: row.createdAt.toISOString(),
    updatedAt: row.updatedAt.toISOString(),
    status: getStockRowStatus(row, { hasBatchedStock: productIsBatched, now }),
  }))

  const allocations: EnrichedAllocationItem[] = rawAllocations.map((a) => {
    const batch = availableBatches.find((b) => b.batchNumber === a.batchNumber)
    return { ...a, batchStatus: batch?.status, expiryDate: batch?.expiryDate }
  })

  return { allocations, availableBatches, stockStatus }
}
